import { type HTMLAttributes } from 'react'
import { cn } from '@lib/utils'
import { useTheme, type Theme } from '@hooks/useTheme'

export interface ThemeToggleProps extends Omit<HTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Size variant */
  size?: 'sm' | 'md' | 'lg'
  /** Show label text */
  showLabel?: boolean
  /** Show system option */
  showSystemOption?: boolean
}

const sizeClasses = {
  sm: 'w-8 h-8 text-body-sm',
  md: 'w-10 h-10 text-body',
  lg: 'w-12 h-12 text-body-lg',
}

/**
 * Sun icon for light mode
 */
function SunIcon({ className }: { className?: string }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <circle cx="12" cy="12" r="5" />
      <line x1="12" y1="1" x2="12" y2="3" />
      <line x1="12" y1="21" x2="12" y2="23" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="1" y1="12" x2="3" y2="12" />
      <line x1="21" y1="12" x2="23" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </svg>
  )
}

/**
 * Moon icon for dark mode
 */
function MoonIcon({ className }: { className?: string }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  )
}

/**
 * Monitor icon for system preference
 */
function MonitorIcon({ className }: { className?: string }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  )
}

/**
 * Simple toggle button between dark and light mode
 *
 * @example
 * <ThemeToggle size="md" />
 */
export function ThemeToggle({
  size = 'md',
  showLabel = false,
  className,
  ...props
}: ThemeToggleProps) {
  const { resolvedTheme, toggleTheme } = useTheme()

  const isDark = resolvedTheme === 'dark'

  return (
    <button
      type="button"
      onClick={toggleTheme}
      className={cn(
        'inline-flex items-center justify-center gap-2',
        'border-2 border-border bg-surface',
        'text-text-primary transition-all duration-fast',
        'hover:border-primary hover:bg-surface-elevated',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        'active:translate-x-[1px] active:translate-y-[1px]',
        sizeClasses[size],
        className
      )}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      {...props}
    >
      <span className="relative w-5 h-5">
        {/* Sun icon - visible in dark mode */}
        <SunIcon
          className={cn(
            'absolute inset-0 transition-all duration-200',
            isDark
              ? 'opacity-100 rotate-0 scale-100'
              : 'opacity-0 -rotate-90 scale-0'
          )}
        />
        {/* Moon icon - visible in light mode */}
        <MoonIcon
          className={cn(
            'absolute inset-0 transition-all duration-200',
            !isDark
              ? 'opacity-100 rotate-0 scale-100'
              : 'opacity-0 rotate-90 scale-0'
          )}
        />
      </span>
      {showLabel && (
        <span className="text-body-sm">
          {isDark ? 'Light' : 'Dark'}
        </span>
      )}
    </button>
  )
}

export interface ThemeSwitcherProps extends HTMLAttributes<HTMLDivElement> {
  /** Size variant */
  size?: 'sm' | 'md'
}

/**
 * Three-way theme switcher with dark, light, and system options
 *
 * @example
 * <ThemeSwitcher />
 */
export function ThemeSwitcher({
  size = 'md',
  className,
  ...props
}: ThemeSwitcherProps) {
  const { theme, setTheme } = useTheme()

  const options: { value: Theme; icon: React.ReactNode; label: string }[] = [
    { value: 'light', icon: <SunIcon className="w-4 h-4" />, label: 'Light' },
    { value: 'system', icon: <MonitorIcon className="w-4 h-4" />, label: 'System' },
    { value: 'dark', icon: <MoonIcon className="w-4 h-4" />, label: 'Dark' },
  ]

  return (
    <div
      role="radiogroup"
      aria-label="Theme selection"
      className={cn(
        'inline-flex border-2 border-border bg-surface',
        className
      )}
      {...props}
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={theme === option.value}
          onClick={() => setTheme(option.value)}
          className={cn(
            'inline-flex items-center justify-center gap-1.5',
            size === 'sm' ? 'px-2 py-1.5 text-caption' : 'px-3 py-2 text-body-sm',
            'transition-colors duration-fast',
            theme === option.value
              ? 'bg-primary text-background'
              : 'text-text-secondary hover:text-text-primary hover:bg-surface-elevated'
          )}
        >
          {option.icon}
          <span className="hidden sm:inline">{option.label}</span>
        </button>
      ))}
    </div>
  )
}

export default ThemeToggle
